/**
 * First-touch signup attribution (GTM §6 follow-up).
 *
 * On landing, stamp utm params + the external referrer into the
 * `ae_attribution` cookie (30 days, first write wins). The server merges it
 * into the signup funnel event at auth time (auth.controller
 * `captureSignupAttribution`) and clears the cookie — so the funnel can
 * answer "which channel did this signup come from" directly.
 *
 * Deliberately cookie-based (not localStorage): the value must survive the
 * OAuth redirect round-trip AND be readable by the server on the callback
 * request, where OAuth signups are created.
 */

import { classifyAcquisitionChannel } from "@reactive-resume/utils";

const COOKIE_NAME = "ae_attribution";
const FIRST_LANDING_KEY = "ae_first_landing";
const LANDING_JOB_KEY = "ae_landing_job_id";
const MAX_AGE_DAYS = 30;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/**
 * Ad-platform click ids. Unlike utm params these are NOT reconstructable after
 * the fact — Google Ads' offline conversion import keys on the exact `gclid`
 * from the click, so a signup captured without one can never be attributed to
 * the ad that paid for it. `gbraid`/`wbraid` are the iOS/app-to-web variants
 * Google sends when `gclid` is unavailable; capture all three.
 */
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;

const TRACKED_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS] as const;

export const captureAttribution = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get("jobId");
    if (jobId && /^[\da-z]+$/.test(jobId)) {
      try {
        localStorage.setItem(LANDING_JOB_KEY, jobId);
      } catch {
        // Attribution still works when storage is blocked.
      }
    }
    // The signup endpoint consumes the attribution cookie. Keep its first landing
    // locally so the signed-in feed can still find the job after that redirect.
    try {
      if (!localStorage.getItem(FIRST_LANDING_KEY)) {
        const raw = document.cookie
          .split("; ")
          .find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`));
        const landing = raw
          ? (
              JSON.parse(decodeURIComponent(raw.slice(COOKIE_NAME.length + 1))) as {
                landing?: unknown;
              }
            ).landing
          : window.location.pathname;
        if (typeof landing === "string" && landing.startsWith("/")) {
          localStorage.setItem(FIRST_LANDING_KEY, landing.slice(0, 200));
        }
      }
    } catch {
      // Storage may be disabled; attribution capture still needs to run.
    }
    // First touch wins — never overwrite an existing attribution.
    if (document.cookie.split("; ").some((c) => c.startsWith(`${COOKIE_NAME}=`))) {
      // The SEO page captured first touch; add the clicked job to that same
      // signup event without replacing its UTM source or external referrer.
      if (jobId && /^[\da-z]+$/.test(jobId)) {
        const raw = document.cookie.split("; ").find((c) => c.startsWith(`${COOKIE_NAME}=`));
        let existing: Record<string, string> | null = null;
        try {
          existing = raw
            ? (JSON.parse(decodeURIComponent(raw.slice(COOKIE_NAME.length + 1))) as Record<
                string,
                string
              >)
            : null;
        } catch {
          // A malformed cookie must never break the page; keep first touch as is.
          existing = null;
        }
        if (existing) {
          existing.jobId = jobId;
          // eslint-disable-next-line unicorn/no-document-cookie, lingui/no-unlocalized-strings
          document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(existing))}; path=/; max-age=${MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax`;
        }
      }
      return;
    }
    const attribution: Record<string, string> = {};

    for (const key of TRACKED_KEYS) {
      const value = params.get(key);
      if (value) attribution[key] = value.slice(0, 200);
    }
    if (jobId && /^[\da-z]+$/.test(jobId)) attribution.jobId = jobId;

    // External referrer only — same-origin navigation is not a channel.
    const referrer = document.referrer;
    if (referrer && !referrer.startsWith(window.location.origin)) {
      attribution.referrer = referrer.slice(0, 500);
    }

    // Classify BEFORE the early return below, so a visit whose only signal is
    // an AI referrer still gets recorded. Answer engines (ChatGPT, Perplexity,
    // Gemini, Copilot, Claude) are a distinct acquisition surface from classic
    // search, and the raw referrer alone left "how many signups did answer
    // engines send us?" unanswerable without eyeballing strings.
    const channel = classifyAcquisitionChannel(attribution.referrer, attribution.utm_source);
    if (channel !== "direct") attribution.channel = channel;

    if (Object.keys(attribution).length === 0) return;

    attribution.landing = window.location.pathname.slice(0, 200);

    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
    // Not UI copy (cookie serialization) and js-cookie isn't a dependency —
    // a single raw write is simpler than adding one.
    // eslint-disable-next-line unicorn/no-document-cookie, lingui/no-unlocalized-strings
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(attribution))}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    // Attribution is best-effort — never let it break the app boot.
  }
};

/** The first path is saved before signup clears ae_attribution. */
export const getFirstLandingPath = (): string | null => {
  try {
    return localStorage.getItem(FIRST_LANDING_KEY);
  } catch {
    return null;
  }
};

export const getLandingJobId = (): string | null => {
  try {
    return localStorage.getItem(LANDING_JOB_KEY);
  } catch {
    return null;
  }
};

/** Forget the stored landing job once it has been handed to the tailor flow. */
export const clearLandingJobId = (): void => {
  try {
    localStorage.removeItem(LANDING_JOB_KEY);
  } catch {
    // Storage may be unavailable; nothing to clear.
  }
};

/** Consume a syntactically valid landing job once onboarding can open tailoring. */
export const consumeLandingJobId = (): string | null => {
  try {
    const jobId = localStorage.getItem(LANDING_JOB_KEY);
    if (jobId) localStorage.removeItem(LANDING_JOB_KEY);
    return jobId && /^[\da-z]+$/.test(jobId) ? jobId : null;
  } catch {
    return null;
  }
};
